// Improved fetchBuilding.jsx - Fixed API URL to match other files

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204'; // Changed from 7084 to 7204

// Rest of your fetchBuilding.jsx code remains the same...
// Helper function to get headers with auth token
const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

// Get building information including creation date
export const getBuildingInfo = async (buildingId) => {
  try {

    const response = await fetch(`${API_BASE_URL}/api/building/${buildingId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });


    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get building info failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Helper function to handle API responses
const handleResponse = async (response) => {
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return await response.json();
    }
    
    return null; // For responses with no content (like PUT)
};

// NEW: Add building for logged-in user
export const addBuildingForUser = async (buildingData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/building/add-for-user`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(buildingData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add building');
    }

    return await response.json();
  } catch (err) {
    throw err;
  }
};

// Get paginated buildings for admin
export const fetchBuildings = async (page = 1, pageSize = 10, search = '') => {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            ...(search && { search })
        });

        const response = await fetch(`${API_BASE_URL}/api/Building/admin/buildings?${queryParams}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get building subscription information
export const fetchSubscription = async (buildingId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Subscription/building/${buildingId}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get building by ID
export const getBuildingById = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Building/getBuildingById?buildingId=${id}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Update building - Enhanced with better error handling
export const updateBuilding = async (id, buildingData) => {
    try {
        // Validate required fields before sending
        if (!buildingData.Address || !buildingData.City) {
            throw new Error('כתובת ועיר הם שדות חובה');
        }

        // Ensure numeric fields are valid
        if (isNaN(buildingData.AddressNumber) || buildingData.AddressNumber <= 0) {
            throw new Error('מספר בית חייב להיות מספר חיובי');
        }
        
        if (isNaN(buildingData.NumFloors) || buildingData.NumFloors <= 0) {
            throw new Error('מספר קומות חייב להיות מספר חיובי');
        }
        
        if (isNaN(buildingData.NumApartments) || buildingData.NumApartments <= 0) {
            throw new Error('מספר דירות חייב להיות מספר חיובי');
        }


        const response = await fetch(`${API_BASE_URL}/api/Building/admin/buildings/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(buildingData)
        });
        
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP ${response.status}: Update building failed`);
        }
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Create new building
export const createBuilding = async (buildingData) => {
    try {
        // Prepare building data with CreateDate and bank account info
        const buildingPayload = {
            Address: buildingData.address,
            City: buildingData.city,
            AddressNumber: parseInt(buildingData.addressNumber),
            NumFloors: parseInt(buildingData.numFloors) || 1,
            NumApartments: parseInt(buildingData.numApartments),
            ZipCode: buildingData.zipCode || null,
            CreateDate: buildingData.createDate ? new Date(buildingData.createDate).toISOString() : new Date().toISOString(),
            // Bank account properties
            AccountNumber: buildingData.accountNumber || null,
            BankNumber: buildingData.bankNumber || null,
            BankName: buildingData.bankName || null,
            BankAccountName: buildingData.bankAccountName || null
        };

        const response = await fetch(`${API_BASE_URL}/api/Building/create`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(buildingPayload)
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get user buildings (existing function) - This now only returns buildings with permissions
export const getUserBuildings = async (userId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Building/getUserBuildings/${userId}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Rest of your validation helper functions remain the same...
export const validateBuilding = (buildingData) => {
    const errors = {};
    
    if (!buildingData.address || buildingData.address.trim().length === 0) {
        errors.address = 'כתובת חובה';
    } else if (buildingData.address.length > 200) {
        errors.address = 'כתובת לא יכולה להיות יותר מ-200 תווים';
    }
    
    if (!buildingData.city || buildingData.city.trim().length === 0) {
        errors.city = 'עיר חובה';
    } else if (buildingData.city.length > 100) {
        errors.city = 'עיר לא יכולה להיות יותר מ-100 תווים';
    }
    
    if (!buildingData.addressNumber || buildingData.addressNumber <= 0) {
        errors.addressNumber = 'מספר בית חייב להיות גדול מ-0';
    }
    
    if (!buildingData.numFloors || buildingData.numFloors < 1 || buildingData.numFloors > 100) {
        errors.numFloors = 'מספר קומות חייב להיות בין 1 ל-100';
    }
    
    if (!buildingData.numApartments || buildingData.numApartments < 1 || buildingData.numApartments > 1000) {
        errors.numApartments = 'מספר דירות חייב להיות בין 1 ל-1000';
    }
    
    if (buildingData.zipCode && buildingData.zipCode.length > 10) {
        errors.zipCode = 'מיקוד לא יכול להיות יותר מ-10 תווים';
    }

    // Validate CreateDate
    if (!buildingData.createDate) {
        errors.createDate = 'תאריך יצירת הבניין חובה';
    } else {
        const createDate = new Date(buildingData.createDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today
        
        if (createDate > today) {
            errors.createDate = 'תאריך יצירת הבניין לא יכול להיות בעתיד';
        }
        
        // Check if date is too far in the past (optional - adjust as needed)
        const hundredYearsAgo = new Date();
        hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
        
        if (createDate < hundredYearsAgo) {
            errors.createDate = 'תאריך יצירת הבניין לא יכול להיות יותר מ-100 שנים בעבר';
        }
    }

    // Validate Bank Account Properties (optional fields)
    if (buildingData.accountNumber && buildingData.accountNumber.length > 20) {
        errors.accountNumber = 'מספר חשבון לא יכול להיות יותר מ-20 תווים';
    }

    if (buildingData.bankNumber && (buildingData.bankNumber.length !== 3 || !/^\d{3}$/.test(buildingData.bankNumber))) {
        errors.bankNumber = 'מספר סניף חייב להיות בדיוק 3 ספרות';
    }

    if (buildingData.bankName && buildingData.bankName.length > 100) {
        errors.bankName = 'שם הבנק לא יכול להיות יותר מ-100 תווים';
    }

    if (buildingData.bankAccountName && buildingData.bankAccountName.length > 100) {
        errors.bankAccountName = 'שם בעל החשבון לא יכול להיות יותר מ-100 תווים';
    }

    // Validate account number format (Israeli bank account - typically 6-7 digits)
    if (buildingData.accountNumber && !/^\d{6,20}$/.test(buildingData.accountNumber)) {
        errors.accountNumber = 'מספר חשבון חייב להכיל רק ספרות (6-20 ספרות)';
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Export all functions as default object
export default {
    fetchBuildings,
    getBuildingById,
    updateBuilding,
    createBuilding,
    getUserBuildings,
    validateBuilding,
    getBuildingInfo
};